import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { listDrafts, type DraftSummary } from '@/smm/api'
import { hydrateDraft } from '@/smm/hydrate'

export const Route = createFileRoute('/')({ component: Dashboard })

function Dashboard() {
  const navigate = useNavigate()
  const [drafts, setDrafts] = useState<DraftSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [opening, setOpening] = useState<string | null>(null)

  useEffect(() => {
    listDrafts().then(setDrafts).catch((e) => setError(String(e)))
  }, [])

  async function open(d: DraftSummary) {
    setError(null)
    setOpening(d.id)
    try {
      await hydrateDraft(d.id, d.projectVersion)
      await navigate({ to: '/editor/$projectId', params: { projectId: d.id } })
    } catch (e) {
      setError(String(e))
      setOpening(null)
    }
  }

  if (error) return <div className="p-8 text-destructive">Failed: {error}</div>
  if (!drafts) return <div className="p-8 text-muted-foreground">Loading drafts…</div>
  if (drafts.length === 0)
    return <div className="p-8 text-muted-foreground">No drafts yet — generate one from the content engine.</div>

  return (
    <div className="mx-auto max-w-3xl p-8">
      <h1 className="mb-6 text-2xl font-semibold">Drafts</h1>
      <ul className="space-y-3">
        {drafts.map((d) => (
          <li
            key={d.id}
            className="flex items-center justify-between rounded-lg border border-border p-4"
          >
            <div>
              <div className="font-medium">Draft {d.id.slice(0, 8)}</div>
              <div className="text-sm text-muted-foreground">
                <span className="capitalize">{d.status}</span> · {d.chosenClipIds.length} clip(s)
                {d.warnings.length > 0 && (
                  <span className="text-amber-500"> · {d.warnings.length} warning(s)</span>
                )}
              </div>
            </div>
            <button
              type="button"
              disabled={opening === d.id}
              onClick={() => open(d)}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
            >
              {opening === d.id ? 'Preparing…' : 'Open'}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
